import { useGameStore } from '../stores/gameStore';
import { useSessionStore } from '../stores/sessionStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { SoftwareType, FileEntry } from '../types';

// Step definitions + progress detection for the first-run tutorial. Kept out of the
// component file so both the coach card (components/Onboarding.tsx) and the navbar
// pointer can use them (and so React fast-refresh stays happy).

/** The entry-point whois NPC every new player starts from. */
export const ENTRY_NODE_IP = '10.0.0.1';
/** The node list looted from the entry node — the player's first set of targets. */
export const SERVER_LIST_FILE = 'servers.txt';
/** Log written on a node you touch; deleting it covers your tracks. */
export const ACCESS_LOG_FILE = 'access.log';
/** The tool the tutorial has the player buy first (single source of truth: step text,
 *  progress detection and the in-Store highlight all read this). */
export const TUTORIAL_SOFTWARE = SoftwareType.Cracker;

export interface OnboardingStep {
  title: string;
  body: string;
  cta: string;
  /** Where the action happens. */
  route: string;
  /** Nav entry to highlight while this step is active. */
  navPath: string;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    title: 'Get your first tool',
    body: `Open the Store, pick the Software category and buy the ${SoftwareType.Cracker} — it breaks into weak nodes. Follow the highlighted buttons.`,
    cta: 'Open Store',
    route: '/store',
    navPath: '/store',
  },
  {
    title: 'Install and run it',
    body: `Wait for the download to finish, then press RUN on the ${SoftwareType.Cracker} in your Local System. A tool only works while it is running.`,
    cta: 'Go to my system',
    route: '/game',
    navPath: '/',
  },
  {
    title: 'Hack your first node',
    body: `In the Remote panel type ${ENTRY_NODE_IP} and attack with BruteForce — that's all the ${SoftwareType.Cracker} needs.`,
    cta: 'Start hacking',
    route: '/game',
    navPath: '/',
  },
  {
    title: 'Steal the node list',
    body: `Press Connect on ${ENTRY_NODE_IP}, then Download ${SERVER_LIST_FILE} — it lists the next targets you can attack.`,
    cta: 'Open the node',
    route: '/game',
    navPath: '/',
  },
  {
    title: 'Cover your tracks',
    body: `Your visit was logged. On the node, Delete ${ACCESS_LOG_FILE} so the owner can't trace you (press Refresh if you don't see it yet) — then Disconnect.`,
    cta: 'Back to the node',
    route: '/game',
    navPath: '/',
  },
  {
    title: 'Pick your next target',
    body: `Open ${SERVER_LIST_FILE} in your Files tab, take an IP from the list and BruteForce it.`,
    cta: 'Start hacking',
    route: '/game',
    navPath: '/',
  },
  {
    title: 'Take a mission',
    body: 'Nodes you own now offer missions — the fastest way to earn credits and XP. Accept your first one.',
    cta: 'Open Missions',
    route: '/missions',
    navPath: '/missions',
  },
];

export interface OnboardingSignals {
  software: { type: SoftwareType; isRunning: boolean; isDownloaded: boolean }[];
  files: FileEntry[];
  activeMissions: number;
  hacked: boolean;
  logDeleted: boolean;
  hackedOther: boolean;
  connected: boolean;
}

/**
 * Which step the player is on, derived from their real state.
 *
 * Evaluated from the LAST achievement backwards, so a player who does things out of
 * order (or who can't complete one step) is carried forward by the next thing they
 * achieve rather than being stuck on an unmet step — the tutorial must never dead-end.
 */
export function onboardingProgress(s: OnboardingSignals): number {
  if (s.activeMissions > 0) return ONBOARDING_STEPS.length;   // all done
  if (s.hackedOther) return 6;                                // → take a mission
  if (s.logDeleted && !s.connected) return 5;                 // → pick the next target
  if (s.files.some(f => f.name === SERVER_LIST_FILE)) return 4; // → cover your tracks
  if (s.hacked) return 3;                                     // → steal the node list
  const hasCracker = s.software.some(x => x.type === SoftwareType.Cracker);
  if (hasCracker && s.software.some(x => x.type === SoftwareType.Cracker && x.isRunning && x.isDownloaded))
    return 2;                                                 // → hack the entry node
  if (hasCracker) return 1;                                   // → run it
  return 0;                                                   // → buy it
}

/**
 * Current step index (or ONBOARDING_STEPS.length when finished). Monotonic: it never
 * moves backwards, so re-connecting to a node after finishing a step doesn't rewind
 * the tutorial.
 */
export function useOnboardingStepIndex(): number {
  const player = useGameStore(s => s.player);
  const session = useSessionStore(s => s.session);
  const hacked = useOnboardingStore(s => s.hacked);
  const logDeleted = useOnboardingStore(s => s.logDeleted);
  const hackedOther = useOnboardingStore(s => s.hackedOther);
  const reached = useOnboardingStore(s => s.step);

  if (!player) return 0;
  const computed = onboardingProgress({
    software: player.software ?? [],
    files: player.files ?? [],
    activeMissions: player.activeMissions?.length ?? 0,
    hacked, logDeleted, hackedOther,
    connected: session != null,
  });
  return Math.max(computed, reached);
}

/**
 * The step the tutorial is currently on, or **null when it isn't running** (skipped,
 * finished, or not a new player). Pages use this to point at the exact control the
 * player needs next — see the Store, which rings the Software tab and then the BUY
 * button of the tool from step 1.
 */
export function useOnboardingActiveStep(): number | null {
  const status = useOnboardingStore(s => s.status);
  const idx = useOnboardingStepIndex();
  const player = useGameStore(s => s.player);
  if (status !== 'active' || !player) return null;
  return idx >= ONBOARDING_STEPS.length ? null : idx;
}

/** Which navbar path the tutorial is pointing at, or null when it isn't running. */
export function useOnboardingNavHint(): string | null {
  const step = useOnboardingActiveStep();
  return step == null ? null : ONBOARDING_STEPS[step].navPath;
}
